<template>
  <div :class="$style.container">
    <!-- MESSAGE -->
    <div :class="[$style.message, { [$style.messageRow]: hideAction }]">
      <!-- @slot Content that will show above the message -->
      <div v-if="$slots['above-message']" :class="$style.aboveMessage">
        <!-- @slot Content that will show above the message -->
        <slot name="above-message" />
      </div>
      <p v-html="message" />
    </div>

    <!-- ACTIONS -->
    <div :class="$style.actions">
      <template v-if="!hideAction">
        <!-- Button for login request -->
        <button
          v-if="actionType === 'login'"
          class="primary-outline fluid"
          :class="$style.button"
          @click="handleLoginRequest"
        >
          {{ actionLabel }}
        </button>

        <!-- Link to actionUrl -->
        <a
          v-else
          class="primary-outline fluid"
          :class="$style.button"
          :href="actionUrl"
        >
          {{ actionLabel }}
        </a>
      </template>

      <!-- Link to secondaryUrl -->
      <a
        v-if="!hideSecondaryAction"
        class="hit-outline fluid"
        :class="$style.button"
        :href="secondaryActionHref"
      >
        {{ secondaryActionLabel }}
      </a>
    </div>
    <!-- POST ACTIONS -->
    <div v-if="$slots['after-action']" :class="$style.afterAction">
      <!-- @slot Content that will show after the buttons -->
      <slot name="after-action" />
    </div>
  </div>
</template>

<script>
import { events, UrlService } from "@nuskin/ns-util";
import { UserService } from "@nuskin/ns-account";
import "@nuskin/ns-core-styles/src/dist/main.css";

export default {
  name: "NsMessageAction",
  props: {
    /**
     * Text to display on button
     */
    actionLabel: {
      type: String,
      default: ""
    },
    /**
     * Pass in 'login' to perform login redirect instead of url redirect
     */
    actionType: {
      type: String,
      default: null,
      validator(value) {
        return ["login"].includes(value);
      }
    },
    /**
     * URL to link too when action button clicked
     */
    actionUrl: {
      type: String,
      default: ""
    },
    /**
     * Message to be displayed
     */
    message: {
      type: String,
      default: ""
    },
    /**
     * Text to display on secondary action
     */
    secondaryActionLabel: {
      type: String,
      default: ""
    },
    /**
     * URL for secondary action
     */
    secondaryActionUrl: {
      type: String,
      default: ""
    },
    /**
     * Flag to use an event to perform secondary action
     */
    useEventForSecondaryAction: Boolean
  },
  data() {
    return {
      /**
       * Used to go to a specific URL if override specified
       */
      secondaryActionPDPSKU: ""
    };
  },
  computed: {
    hideAction() {
      return this.actionType === "login" && UserService.isLoggedIn();
    },
    hideSecondaryAction() {
      return !this.secondaryActionLabel;
    },
    secondaryActionHref() {
      return this.useEventForSecondaryAction || !this.secondaryActionUrl
        ? `${UrlService.getAemLocaleUrl()}products/product.${
            this.secondaryActionPDPSKU
          }.html`
        : this.secondaryActionUrl;
    }
  },
  mounted() {
    events.subscribe(events.shop.SHOW_EXCLUSIVE_OFFER_DETAILS, item => {
      if (item && item.sku) {
        this.secondaryActionPDPSKU = item.sku;
      }
    });
  },
  methods: {
    /**
     * Request login
     */
    handleLoginRequest() {
      events.publish(events.authentication.REQUEST_FOR_LOGIN);
    }
  }
};
</script>

<style lang="scss" module>
@use "sass:map";
@use '~@nuskin/ns-core-styles/src/scss/global.mixins';
@use '~@nuskin/ns-core-styles/src/scss/colors';

.container {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding: 15px 15px;
  padding-top: 20px;
  width: 100%;
  background-color: rgba(147, 0, 147, 0.06);
}

.message {
  display: flex;
  flex-direction: column;
  color: map.get(colors.$themeColors, color-primary-text);
  @include global.p-medium();

  &.messageRow {
    width: 100%;
  }
}

.aboveMessage {
  margin-bottom: 10px;
}

.actions {
  margin-top: 10px;
}

.button,
a.button {
  margin-top: 15px;
  white-space: nowrap;

  &:first-child {
    margin-top: 0;
  }
}

.afterAction {
  margin-top: 10px;
}

@media (min-width: 768px) {
  .messageRow {
    flex-direction: row;
    justify-content: space-between;

    .aboveMessage {
      margin-right: 60px;
    }
  }
}

@media (min-width: 1024px) {
  .container {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    padding: 10px 17px;
    padding-top: 15px;
  }

  .message {
    max-width: 313px;

    &.messageRow {
      max-width: none;
    }
  }

  .actions {
    margin-top: 0;
    margin-left: 15px;
  }

  .afterAction {
    margin-top: 0;
    margin-left: 15px;
  }
}
</style>
